/**
 * SupplierReportedAmountsPage - View all reported amounts/line items for a supplier
 *
 * UI/UX DESCRIPTION:
 * - Header with supplier name and back navigation
 * - Summary stats cards showing totals and counts
 * - Invoice summary table with volume breakdown per invoice
 * - Filters for date range, division, invoice number, and rebateable status
 * - Line items table with product group, tags, and rule associations
 *
 * FUNCTIONALITY:
 * - View all invoice line items for a specific supplier
 * - See which rebate rules/agreements each line item affects
 * - Filter by date range, division, invoice number, and rebateable/non-rebateable
 * - Pagination for large datasets
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from '@tanstack/react-router';
import { format } from 'date-fns';
import {
  ArrowLeftIcon,
  ReceiptIcon,
  CurrencyDollarIcon,
  FileTextIcon,
  PackageIcon,
  FunnelIcon,
  CircleNotchIcon,
  CalendarIcon,
  ArrowSquareOutIcon,
} from '@phosphor-icons/react';
import DashboardLayout from 'src/layouts/dashboard/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import GentlyTable, { type TableColumn } from '@/components/common/GentlyTable';
import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/common/MultiSelect';
import { useSupplier } from '@/api/rpc/hooks/accounts-payable/useSupplier';
import useDivisions from '@/api/organization/useDivisions';
import { useGetAgreementsForSupplier } from '@/api/rpc/hooks/rebates/useHistoricalReportedImport';
import {
  useSupplierInvoiceLineItems,
  type EnrichedLineItem,
  type InvoiceSummary,
  type SupplierInvoiceLineItemsFilters,
} from '@/api/rebates/useSupplierInvoiceLineItems';
import { formatCurrency, formatPercentage } from '@erp/shared-utils';
import { formatPaymentDate } from 'src/utils/dateUtils';
import { cn } from '@/lib/utils';
import { VendorReportedAmountsTables } from '@/components/rebates/VendorReportedAmountsTables';
import type { RebateInvoice } from '@/api/rebates/types';
import { useGetDocument } from '@/api/core/documents/useGetDocument';
import { useSupplierInvoiceDeleteModal } from '@/hooks/useSupplierInvoiceDeleteModal';
import { DeleteRebateInvoiceModal } from '@/components/rebates/DeleteRebateInvoiceModal';

function SupplierInvoicesPage() {
  const params = useParams({
    from: '/authenticated/rebates/supplier-reported-amounts/$supplierId',
  });
  const supplierId = params.supplierId;
  const navigate = useNavigate();

  // Filter state - ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [selectedDivisions, setSelectedDivisions] = useState<
    MultiSelectOption[]
  >([]);
  const [selectedInvoiceNumbers, setSelectedInvoiceNumbers] = useState<
    MultiSelectOption[]
  >([]);
  const [showNonRebateable, setShowNonRebateable] = useState(true);

  // Fetch supplier details - hook handles undefined supplierId internally
  const { data: supplier, isLoading: supplierLoading } = useSupplier(
    typeof supplierId === 'string' ? supplierId : undefined,
  );

  // Fetch divisions for filter
  const { data: divisionsData } = useDivisions(true);

  // Build filters - fetch all data, handle sorting/pagination client-side
  const filters = useMemo(() => {
    const f: SupplierInvoiceLineItemsFilters = {
      limit: 10000, // Fetch all data - sorting/pagination handled client-side
      offset: 0,
    };

    if (startDate) {
      f.startDate = format(startDate, 'yyyy-MM-dd');
    }
    if (endDate) {
      f.endDate = format(endDate, 'yyyy-MM-dd');
    }
    if (selectedDivisions.length > 0) {
      f.divisionIds = selectedDivisions.map((d) => d.id);
    }
    if (selectedInvoiceNumbers.length > 0) {
      f.invoiceNumber = selectedInvoiceNumbers.map(
        (inv) => inv.value as string,
      );
    }
    if (!showNonRebateable) {
      f.rebateableOnly = true;
    }

    return f;
  }, [
    startDate,
    endDate,
    selectedDivisions,
    selectedInvoiceNumbers,
    showNonRebateable,
  ]);

  // Fetch all invoice numbers separately (without any filters except supplierId)
  // This ensures we always have all invoice options available regardless of other filters
  const allDataFilters = useMemo(() => {
    return {
      limit: 10000, // Fetch all data
      offset: 0,
    };
  }, []);

  // Fetch line items - use conditional enabling to handle invalid supplierId
  const isValidSupplierId = !!supplierId && typeof supplierId === 'string';

  // Fetch agreements for supplier
  const { data: agreementsData, isLoading: agreementsLoading } =
    useGetAgreementsForSupplier(
      typeof supplierId === 'string' ? supplierId : null,
    );

  const {
    data,
    isLoading,
    error,
    refetch: refetchLineItems,
  } = useSupplierInvoiceLineItems(
    isValidSupplierId ? supplierId : '',
    filters,
    isValidSupplierId,
  );

  // Fetch all data without filters (for invoice number dropdown options and volume tables)
  const { data: allData, refetch: refetchAllData } =
    useSupplierInvoiceLineItems(
      isValidSupplierId ? supplierId : '',
      allDataFilters,
      isValidSupplierId,
    );

  // Document fetching - on-demand POST request
  const { mutate: fetchDocument } = useGetDocument();

  // Invoice delete modal
  const invoiceDeleteModal = useSupplierInvoiceDeleteModal({
    onSuccess: () => {
      // Refresh both queries after deletion
      void refetchLineItems();
      void refetchAllData();
    },
  });

  // Handler for viewing documents
  const handleViewDocument = useCallback(
    (documentId: string, fileName?: string) => {
      if (!documentId) {
        return;
      }
      fetchDocument(
        { documentId: documentId },
        {
          onSuccess: (response) => {
            if (response.data.signedUrl) {
              window.open(response.data.signedUrl, '_blank');
            }
          },
          onError: () => {
            // Error handling is done in the mutation hook
          },
        },
      );
    },
    [fetchDocument],
  );

  // Division options for filter
  const divisionOptions: MultiSelectOption[] = useMemo(() => {
    if (!divisionsData?.divisions) return [];
    return divisionsData.divisions.map((d) => ({
      id: d.id,
      label: d.name,
      value: d.id,
    }));
  }, [divisionsData]);

  // Invoice number options for filter - use all invoices data, not filtered data
  const invoiceNumberOptions: MultiSelectOption[] = useMemo(() => {
    if (!allData?.invoiceSummaries) return [];
    const uniqueInvoices = new Map<string, string>();
    allData.invoiceSummaries.forEach((inv) => {
      if (!uniqueInvoices.has(inv.invoiceNumber)) {
        uniqueInvoices.set(inv.invoiceNumber, inv.invoiceNumber);
      }
    });
    return Array.from(uniqueInvoices.entries())
      .map(([invoiceNumber]) => ({
        id: invoiceNumber,
        label: invoiceNumber,
        value: invoiceNumber,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allData?.invoiceSummaries]);

  // Table columns
  const columns: TableColumn<EnrichedLineItem>[] = useMemo(
    () => [
      {
        id: 'date' as keyof EnrichedLineItem,
        label: 'Date',
        sortable: true,
        render: (value: string) => (
          <span className="text-sm font-medium">
            {formatPaymentDate(value)}
          </span>
        ),
      },
      {
        id: 'invoiceNumber' as keyof EnrichedLineItem,
        label: 'Invoice #',
        sortable: true,
        render: (value: string) => (
          <span className="text-sm font-mono">{value}</span>
        ),
      },
      {
        id: 'divisionName' as keyof EnrichedLineItem,
        label: 'Division',
        sortable: true,
        render: (value: string | null) => (
          <span className="text-sm">{value || '-'}</span>
        ),
      },
      {
        id: 'productGroupName' as keyof EnrichedLineItem,
        label: 'Product Group',
        sortable: true,
        render: (value: string | null, row: EnrichedLineItem) => (
          <div className="flex flex-col gap-1">
            <span
              className={`text-sm ${value ? 'font-medium' : 'text-muted-foreground italic'}`}
            >
              {value || 'Non-Rebateable'}
            </span>
            {row.productGroupTags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {row.productGroupTags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="secondary"
                    className="text-xs px-1.5 py-0"
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'value' as keyof EnrichedLineItem,
        label: 'Value',
        sortable: true,
        align: 'right',
        render: (value: number, row: EnrichedLineItem) => (
          <span
            className={`text-sm font-medium ${row.productGroupId ? 'text-green-600' : 'text-muted-foreground'}`}
          >
            {formatCurrency(value)}
          </span>
        ),
      },
      {
        id: 'ruleAssociations' as keyof EnrichedLineItem,
        label: 'Rebate Rules',
        render: (_value: unknown, row: EnrichedLineItem) => {
          if (row.ruleAssociations.length === 0) {
            return <span className="text-sm text-muted-foreground">-</span>;
          }

          // Group by agreement
          const byAgreement = new Map<string, typeof row.ruleAssociations>();
          for (const assoc of row.ruleAssociations) {
            const existing = byAgreement.get(assoc.agreementId) || [];
            existing.push(assoc);
            byAgreement.set(assoc.agreementId, existing);
          }

          return (
            <div className="flex flex-wrap gap-1 max-w-md">
              {Array.from(byAgreement.entries()).map(([agreementId, rules]) => (
                <TooltipProvider key={agreementId}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        to="/rebates/agreements/$id"
                        params={{ id: agreementId }}
                        className="inline-block"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Badge
                          variant="outline"
                          className="text-xs cursor-pointer hover:bg-accent"
                        >
                          {rules[0].agreementName}
                          {rules.length > 1 && ` (${rules.length} rules)`}
                        </Badge>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs">
                      <div className="space-y-1">
                        <p className="font-medium">{rules[0].agreementName}</p>
                        {rules.map((rule) => (
                          <div key={rule.ruleId} className="text-xs">
                            <span className="font-medium">{rule.ruleName}</span>
                            <span className="text-muted-foreground ml-1">
                              (
                              {rule.isQualifying && rule.isAffected
                                ? 'Q+A'
                                : rule.isQualifying
                                  ? 'Qualifying'
                                  : 'Affected'}
                              )
                            </span>
                          </div>
                        ))}
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
            </div>
          );
        },
      },
    ],
    [],
  );

  // Handle back navigation
  function handleBack() {
    navigate({ to: '/rebates/agreements' });
  }

  // Handle filter reset
  function handleClearFilters() {
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedDivisions([]);
    setSelectedInvoiceNumbers([]);
    setShowNonRebateable(true);
  }

  // Check if any filters are active
  const hasActiveFilters =
    startDate ||
    endDate ||
    selectedDivisions.length > 0 ||
    selectedInvoiceNumbers.length > 0 ||
    !showNonRebateable;

  // Runtime guard to validate supplierId - MUST BE AFTER ALL HOOKS
  if (!supplierId || typeof supplierId !== 'string') {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                <p>Invalid supplier ID.</p>
                <Button
                  variant="outline"
                  onClick={() => navigate({ to: '/rebates/agreements' })}
                  className="mt-4"
                >
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to Agreements
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Loading state
  if (supplierLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <CircleNotchIcon className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !supplierId) {
    return (
      <DashboardLayout>
        <div className="p-6">
          <Card>
            <CardContent className="p-6">
              <div className="text-center text-muted-foreground">
                <p>Failed to load supplier reported amounts.</p>
                <Button variant="outline" onClick={handleBack} className="mt-4">
                  <ArrowLeftIcon className="h-4 w-4 mr-2" />
                  Back to Agreements
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={handleBack}>
            <ArrowLeftIcon className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">
              {supplier?.name || 'Supplier'} - Reported Amounts
            </h1>
            <p className="text-sm text-muted-foreground">
              View all reported line items and their rebate rule associations
            </p>
          </div>
        </div>

        {/* Summary Cards */}
        {data?.summary && (
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Total Volume
                </CardTitle>
                <CurrencyDollarIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(data.summary.totalVolume)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.summary.totalLineItems.toLocaleString()} line items
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Rebateable
                </CardTitle>
                <PackageIcon className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(data.summary.rebateableVolume)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.summary.totalVolume > 0
                    ? `${((data.summary.rebateableVolume / data.summary.totalVolume) * 100).toFixed(1)}% of total`
                    : '0%'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Invoices</CardTitle>
                <ReceiptIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data.summary.uniqueInvoices.toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">Unique invoices</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">
                  Agreements
                </CardTitle>
                <FileTextIcon className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {data.summary.uniqueAgreements}
                </div>
                <p className="text-xs text-muted-foreground">
                  {data.summary.uniqueRules} rules
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Agreements Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileTextIcon className="h-5 w-5" />
              Agreements
            </CardTitle>
          </CardHeader>
          <CardContent>
            {agreementsLoading ? (
              <div className="flex items-center justify-center py-8">
                <CircleNotchIcon className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : agreementsData?.agreements &&
              agreementsData.agreements.length > 0 ? (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Agreement Name</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {agreementsData.agreements.map((agreement) => (
                      <TableRow key={agreement.id}>
                        <TableCell>
                          <span className="font-medium text-sm">
                            {agreement.name}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm whitespace-nowrap">
                            {new Intl.DateTimeFormat('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              timeZone: 'UTC',
                            }).format(new Date(agreement.startDate))}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm whitespace-nowrap">
                            {new Intl.DateTimeFormat('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              timeZone: 'UTC',
                            }).format(new Date(agreement.endDate))}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              window.open(
                                `/rebates/agreements/${agreement.id}`,
                                '_blank',
                              );
                            }}
                          >
                            <ArrowSquareOutIcon className="h-4 w-4 mr-2" />
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <p>No agreements found for this supplier</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FunnelIcon className="h-5 w-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div>
                <Label className="mb-1.5 block">Start Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-8',
                        !startDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? (
                        <span>{format(startDate, 'MM/dd/yyyy')}</span>
                      ) : (
                        <span>Select start date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => {
                        setStartDate(date);
                      }}
                      defaultMonth={startDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="mb-1.5 block">End Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal h-8',
                        !endDate && 'text-muted-foreground',
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDate ? (
                        <span>{format(endDate, 'MM/dd/yyyy')}</span>
                      ) : (
                        <span>Select end date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => {
                        setEndDate(date);
                      }}
                      defaultMonth={endDate || startDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="mb-1.5 block">Divisions</Label>
                <MultiSelect
                  options={divisionOptions}
                  value={selectedDivisions}
                  onChange={(value) => {
                    setSelectedDivisions(value);
                  }}
                  placeholder="All divisions"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Reported Amount IDs</Label>
                <MultiSelect
                  options={invoiceNumberOptions}
                  value={selectedInvoiceNumbers}
                  onChange={(value) => {
                    setSelectedInvoiceNumbers(value);
                  }}
                  placeholder="All IDs"
                  liveUpdate={false}
                />
              </div>
            </div>
            <div className="mt-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-non-rebateable"
                  checked={showNonRebateable}
                  onCheckedChange={(checked) => {
                    setShowNonRebateable(checked);
                  }}
                />
                <Label
                  htmlFor="show-non-rebateable"
                  className="text-sm font-normal cursor-pointer"
                >
                  Show Non-Rebateable Items
                </Label>
              </div>
            </div>
            {hasActiveFilters && (
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearFilters}
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Vendor Reported Amounts Tables */}
        {(data?.lineItems && data.lineItems.length > 0) ||
        (data?.invoiceSummaries && data.invoiceSummaries.length > 0) ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ReceiptIcon className="h-5 w-5" />
                Vendor Reported Amounts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VendorReportedAmountsTables
                invoices={
                  // Use allData for invoice summaries to get complete date ranges
                  allData?.invoiceSummaries
                    ? allData.invoiceSummaries.map((inv) => {
                        return {
                          id: inv.invoiceId,
                          invoiceNumber: inv.invoiceNumber,
                          totalAmount: inv.totalVolume,
                          totalRebateableAmount: inv.rebateableVolume,
                          currency: 'USD',
                          invoiceDate: inv.date,
                          lineItemDateRange: inv.lineItemDateRange || null,
                          supplier: {
                            id: supplierId,
                            name: supplier?.name || 'Unknown',
                          },
                          rebateSummary: {
                            totalQualifyingVolume: 0,
                            totalAffectedVolume: 0,
                            ruleCount: 0,
                          },
                          createdAt: inv.createdAt || inv.date,
                          updatedAt: inv.createdAt || inv.date,
                          files: inv.files,
                        } as RebateInvoice;
                      })
                    : undefined
                }
                invoicesLoading={isLoading}
                // Use allData for line items to show all months in volume tables
                // (allData fetches with limit 1000, no filters, so all months are included)
                lineItems={allData?.lineItems}
                lineItemsLoading={isLoading}
                isCumulative={false}
                selectedDivisionIds={
                  selectedDivisions.length > 0
                    ? new Set(selectedDivisions.map((d) => d.id))
                    : undefined
                }
                showNonRebateable={showNonRebateable}
                onShowNonRebateableChange={setShowNonRebateable}
                onViewDocument={handleViewDocument}
                onDeleteInvoice={invoiceDeleteModal.initiateDelete}
                hideInvoicesTable={false}
              />
            </CardContent>
          </Card>
        ) : null}

        {/* Line Items Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Line Items</CardTitle>
          </CardHeader>
          <CardContent>
            <GentlyTable
              data={data?.lineItems || []}
              columns={columns}
              isLoading={isLoading}
              emptyMessage="No reported amounts found for this supplier"
              showSearch={true}
              searchPlaceholder="Search by division, product group, invoice..."
              searchFields={[
                'divisionName',
                'productGroupName',
                'invoiceNumber',
              ]}
              showDownload={true}
              downloadFilename={`${supplier?.name || 'supplier'}-reported-amounts`}
              maxHeight={600}
              stickyHeader
            />
          </CardContent>
        </Card>
      </div>

      {/* Delete Invoice Modal */}
      <DeleteRebateInvoiceModal
        open={invoiceDeleteModal.modalOpen}
        invoiceInfo={invoiceDeleteModal.invoiceInfo}
        loading={invoiceDeleteModal.isDeleting}
        showAdvanced={invoiceDeleteModal.showAdvanced}
        forceDeleteConfirmText={invoiceDeleteModal.forceDeleteConfirmText}
        onSetShowAdvanced={invoiceDeleteModal.setShowAdvanced}
        onSetForceDeleteConfirmText={
          invoiceDeleteModal.setForceDeleteConfirmText
        }
        onConfirmDelete={invoiceDeleteModal.confirmDelete}
        onCancel={invoiceDeleteModal.closeModal}
      />
    </DashboardLayout>
  );
}

export default SupplierInvoicesPage;
